<template>
  <lila-default-template>
    <template #mainContent>
      <div class="container reports-page">
        <div class="widgets">
          <!-- Cabeçalho -->
          <div class="header-with-refresh mb2">
            <p class="mb1 text-has-font-35 has-text-weight-semibold">
              Bem-vindo!
            </p>

            <div class="header-refresh-box">
              <button
                type="button"
                class="btn-refresh"
                :disabled="refreshing"
                @click="reloadAllWidgets()"
              >
                {{ refreshing ? "Atualizando…" : "Atualizar" }}
              </button>
              <p class="text-has-font-12">
                Atualizado última vez em:
                <time class="has-text-weight-semibold">{{ lastUpdate }}</time>
              </p>
            </div>
          </div>

          <!-- POC Cuidados Paliativos: seletor de visão do relatório -->
          <div
            v-if="hasPalliativeCare"
            class="report-view-switch mb2"
            role="tablist"
            aria-label="Visão do relatório"
          >
            <button
              v-for="view in reportViews"
              :key="view.key"
              type="button"
              role="tab"
              class="report-view-option"
              :class="{ 'is-active': reportView === view.key }"
              :aria-selected="reportView === view.key"
              @click="changeReportView(view.key)"
            >
              {{ view.label }}
            </button>
          </div>

          <palliative-care-report v-if="hasPalliativeCare && reportView === 'cp'" />

          <div
            v-else
            class="columns"
            :class="refreshing ? 'loading large reports-refreshing' : ''"
          >
            <!-- Coluna esquerda: dados da instituição -->
            <div class="column column-flex is-6">
              <p class="mb2">
                Acesse aqui os dados da sua instituição
              </p>

              <unit-filter-select
                class="mb4"
                :model-value="selectedUnitId"
                :options="unitOptions"
                :default-label="defaultOptionLabel"
                :can-select-institution="canSelectInstitution"
                :loading="loadingUnits"
                :busy="refreshing"
                :disabled="refreshing"
                @update:model-value="changeUnit"
              />

              <div
                v-if="scopeAlert"
                class="notification reports-scope-alert"
              >
                <button
                  type="button"
                  class="delete"
                  @click="dismissScopeAlert"
                />
                {{ scopeAlert }}
              </div>

              <div class="columns">
                <div class="column">
                  <!-- Resumo Profissionais / Pacientes -->
                  <section
                    class="widget hover-animated doctors-patients-summary"
                  >
                    <div class="widget-content">
                      <div class="summary-box">
                        <div class="summary has-text-centered">
                          <p class="summary-qtd has-text-weight-light">
                            {{ summary.doctor }}
                          </p>
                          <div class="mt1 mb1 summary-divider" />
                          <p class="summary-title has-text-weight-bold">
                            Profissionais
                          </p>
                        </div>

                        <div class="summary has-text-centered">
                          <p class="summary-qtd has-text-weight-light">
                            {{ summary.patient }}
                          </p>
                          <div class="mt1 mb1 summary-divider" />
                          <p class="summary-title has-text-weight-bold">
                            Pacientes
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>

              <div class="columns adjust-height">
                <!-- Performance Status -->
                <div class="column is-6">
                  <section
                    class="widget full-height hover-animated performance-status"
                  >
                    <div class="widget-content">
                      <header>
                        <h1 class="has-text-weight-bold">
                          Performance Status
                        </h1>
                        <button
                          class="btn-widget-detail"
                          title="Ver detalhes"
                          @click="openDetail('performance')"
                        >
                          <div />
                          <div />
                          <div />
                        </button>
                      </header>

                      <div class="chart-box">
                        <div
                          v-for="ps in performanceStatus"
                          :key="ps.key"
                          class="vertical-line-chart"
                        >
                          <div class="chart-header">
                            <p class="chart-title">
                              {{ ps.key }}
                            </p>
                            <p class="chart-value">
                              {{ fmtPct(ps.percentage) }}%
                            </p>
                          </div>
                          <div class="chart-line">
                            <div
                              class="chart-value"
                              :class="`bg-${ps.key.toLowerCase()}`"
                              :style="{ width: `${ps.percentage}%` }"
                            />
                          </div>
                        </div>
                        <p
                          v-if="performanceStatus.length === 0"
                          class="widget-empty"
                        >
                          {{
                            errors.performance
                              ? "Não foi possível carregar."
                              : "Carregando…"
                          }}
                        </p>
                      </div>
                    </div>
                  </section>
                </div>

                <!-- Tratamento -->
                <div class="column is-6">
                  <section class="widget full-height hover-animated treatment">
                    <div class="widget-content">
                      <header>
                        <h1 class="has-text-weight-bold">
                          Tratamento
                        </h1>
                        <button
                          class="btn-widget-detail"
                          title="Ver detalhes"
                          @click="openDetail('treatment')"
                        >
                          <div />
                          <div />
                          <div />
                        </button>
                      </header>

                      <div class="chart-box mb5 doughnut-box">
                        <lila-doughnut-chart
                          v-if="treatment.data"
                          :chart-data="treatment.data"
                          :options="doughnutOptions"
                        />
                        <p
                          v-else
                          class="widget-empty"
                        >
                          {{
                            errors.treatment
                              ? "Não foi possível carregar."
                              : "Carregando…"
                          }}
                        </p>
                      </div>

                      <div class="chart-legend">
                        <ul>
                          <li
                            v-for="item in treatment.legend"
                            :key="item.label"
                            class="has-text-weight-medium"
                          >
                            <span
                              class="legend-dot"
                              :style="{ backgroundColor: item.color }"
                            />
                            {{ item.label }}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>

            <!-- Coluna direita: últimos 21 dias -->
            <div class="column column-flex is-6">
              <p class="mb4 has-text-weight-bold">
                Últimos 21 (vinte e um) dias
              </p>

              <div class="columns">
                <div class="column column-flex is-6">
                  <div class="columns adjust-height">
                    <!-- Profissionais ativos -->
                    <div class="column is-6">
                      <section
                        class="widget full-height hover-animated active-doctors-new-patients active-doctors"
                      >
                        <div class="widget-content">
                          <header class="only-btn">
                            <button
                              class="btn-widget-detail"
                              title="Ver detalhes"
                              @click="openDetail('active')"
                            >
                              <div />
                              <div />
                              <div />
                            </button>
                          </header>
                          <div>
                            <p
                              class="text-has-font-35 has-text-weight-light has-text-centered"
                            >
                              {{ fmtPct(activeDoctorsPercentage) }}%
                            </p>
                            <div class="mt1 mb1 summary-divider" />
                            <div
                              class="chart-title has-text-weight-medium text-has-font-14"
                            >
                              Dos profissionais ativos
                            </div>
                          </div>
                        </div>
                      </section>
                    </div>

                    <!-- Novos pacientes -->
                    <div class="column is-6">
                      <section
                        class="widget full-height hover-animated active-doctors-new-patients"
                      >
                        <div class="widget-content">
                          <header class="only-btn">
                            <button
                              class="btn-widget-detail"
                              title="Ver detalhes"
                              @click="openDetail('newpatients')"
                            >
                              <div />
                              <div />
                              <div />
                            </button>
                          </header>
                          <div>
                            <p
                              class="text-has-font-40 has-text-weight-light has-text-centered"
                            >
                              {{
                                newPatientsTotal > 0
                                  ? `+${newPatientsTotal}`
                                  : 0
                              }}
                            </p>
                            <div class="mt1 mb1 summary-divider" />
                            <div
                              class="chart-title has-text-weight-medium text-has-font-16 has-text-centered"
                            >
                              Novos Pacientes
                            </div>
                          </div>
                        </div>
                      </section>
                    </div>
                  </div>
                </div>

                <!-- Distribuição de CIDs -->
                <div class="column is-6">
                  <section
                    class="widget full-height hover-animated cid-distribution"
                  >
                    <div class="widget-content">
                      <header>
                        <h1 class="has-text-weight-bold">
                          Distribuição de CIDS
                        </h1>
                        <button
                          class="btn-widget-detail"
                          title="Ver detalhes"
                          @click="openDetail('cid')"
                        >
                          <div />
                          <div />
                          <div />
                        </button>
                      </header>

                      <div class="chart-box">
                        <div
                          v-for="cid in cidDistribution"
                          :key="cid.code"
                          class="vertical-line-chart"
                        >
                          <p class="chart-title text-has-font-16">
                            {{ cid.code }}
                          </p>
                          <div class="chart-line">
                            <div
                              class="chart-value has-bg-dark-purple"
                              :style="{ width: `${cid.percentage}%` }"
                            />
                          </div>
                          <p class="chart-value text-has-font-16">
                            {{ fmtPct(cid.percentage) }}%
                          </p>
                        </div>
                        <p
                          v-if="cidDistribution.length === 0"
                          class="widget-empty"
                        >
                          {{
                            errors.cids
                              ? "Não foi possível carregar."
                              : "Carregando…"
                          }}
                        </p>
                      </div>
                    </div>
                  </section>
                </div>
              </div>

              <!-- Demandas / indicadores em linha -->
              <div class="columns adjust-height">
                <div class="column demands-stack">
                  <!-- Dor moderada ou grave -->
                  <section class="widget mb1 hover-animated patient-demands">
                    <div class="widget-content">
                      <div class="chart-box">
                        <div class="vertical-line-chart">
                          <p
                            class="chart-value text-has-font-16 has-text-dark-purple has-text-weight-bold"
                          >
                            {{ painPatients.total }}
                          </p>
                          <p class="chart-title text-has-font-14">
                            Pacientes com dor moderada ou grave
                          </p>
                          <div class="chart-line-box">
                            <div class="chart-value">
                              {{ fmtPct(painPatients.percentage) }}%
                            </div>
                            <div class="chart-line">
                              <div
                                class="chart-value has-bg-dark-purple"
                                :style="{
                                  width: `${painPatients.percentage}%`
                                }"
                              />
                            </div>
                          </div>
                          <button
                            class="btn-widget-detail"
                            title="Ver detalhes"
                            @click="openDetail('pain')"
                          >
                            <div />
                            <div />
                            <div />
                          </button>
                        </div>
                      </div>
                    </div>
                  </section>

                  <!-- Febre -->
                  <section class="widget mb1 hover-animated patient-demands">
                    <div class="widget-content">
                      <div class="chart-box">
                        <div class="vertical-line-chart">
                          <p
                            class="chart-value text-has-font-16 has-text-dark-purple has-text-weight-bold"
                          >
                            {{ feverPatients.total }}
                          </p>
                          <p class="chart-title text-has-font-14">
                            Pacientes com febre superior a 37.8°
                          </p>
                          <div class="chart-line-box">
                            <div class="chart-value">
                              {{ fmtPct(feverPatients.percentage) }}%
                            </div>
                            <div class="chart-line">
                              <div
                                class="chart-value has-bg-dark-purple"
                                :style="{
                                  width: `${feverPatients.percentage}%`
                                }"
                              />
                            </div>
                          </div>
                          <button
                            class="btn-widget-detail"
                            title="Ver detalhes"
                            @click="openDetail('fever')"
                          >
                            <div />
                            <div />
                            <div />
                          </button>
                        </div>
                      </div>
                    </div>
                  </section>

                  <!-- Relatos semanais -->
                  <section class="widget mb1 hover-animated patient-demands">
                    <div class="widget-content">
                      <div class="chart-box">
                        <div class="vertical-line-chart">
                          <p
                            class="chart-value text-has-font-16 has-text-dark-purple has-text-weight-bold"
                          >
                            {{ weeklyReports.total }}
                          </p>
                          <p class="chart-title text-has-font-14">
                            Relatos semanais atualizados
                          </p>
                          <div class="chart-line-box">
                            <div class="chart-value">
                              {{ fmtPct(weeklyReports.percentage) }}%
                            </div>
                            <div class="chart-line">
                              <div
                                class="chart-value has-bg-dark-purple"
                                :style="{
                                  width: `${weeklyReports.percentage}%`
                                }"
                              />
                            </div>
                          </div>
                          <div class="btn-widget-detail" />
                        </div>
                      </div>
                    </div>
                  </section>

                  <!-- Sintomas relatados -->
                  <section class="widget mb1 hover-animated patient-demands">
                    <div class="widget-content">
                      <div class="chart-box">
                        <div class="vertical-line-chart">
                          <p
                            class="chart-value text-has-font-16 has-text-dark-purple has-text-weight-bold"
                          >
                            {{ reportedSymptoms.total }}
                          </p>
                          <p class="chart-title text-has-font-14">
                            Pacientes relataram sintomas
                          </p>
                          <div class="chart-line-box">
                            <div class="chart-value">
                              {{ fmtPct(reportedSymptoms.percentage) }}%
                            </div>
                            <div class="chart-line">
                              <div
                                class="chart-value has-bg-dark-purple"
                                :style="{
                                  width: `${reportedSymptoms.percentage}%`
                                }"
                              />
                            </div>
                          </div>
                          <div class="btn-widget-detail" />
                        </div>
                      </div>
                    </div>
                  </section>

                  <!-- Demandas de comunicação -->
                  <section class="widget patient-demands">
                    <div class="widget-content">
                      <div class="only-text">
                        <p
                          class="text-value text-has-font-16 has-text-dark-purple has-text-weight-bold"
                        >
                          {{ communicationDemands.total }}
                        </p>
                        <p class="text-title text-has-font-14">
                          Demandas de comunicação
                        </p>
                        <p class="text-subtitle text-has-font-14">
                          Média de
                          <strong>{{ communicationDemands.average }}</strong>
                          por paciente
                        </p>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal de detalhe (aberto pelo botão de 3 pontos).
             Teleportado para o <body> para o overlay cobrir a tela inteira,
             sem ficar preso ao contexto de empilhamento do template.
             O wrapper .reports-page mantém válidos os seletores de estilo. -->
        <teleport to="body">
          <div
            v-if="activeModal"
            class="reports-page rp-modal-root"
          >
            <div class="rp-modal">
              <div
                class="rp-modal-bg"
                @click="closeDetail"
              />
              <div
                class="rp-modal-card"
                :class="{
                  'rp-modal-card--wide':
                    activeModal === 'treatment' ||
                    activeModal === 'performance' ||
                    activeModal === 'active' ||
                    activeModal === 'newpatients' ||
                    activeModal === 'cid' ||
                    activeModal === 'pain' ||
                    activeModal === 'fever',
                  'rp-modal-card--cid': activeModal === 'cid',
                  'rp-modal-card--symptom':
                    activeModal === 'pain' || activeModal === 'fever'
                }"
              >
                <div class="rp-modal-head">
                  <p class="rp-modal-title">
                    {{ modalTitle }}
                  </p>
                  <button
                    type="button"
                    class="rp-modal-close"
                    @click="closeDetail"
                  >
                    <img
                      :src="require('@assets/images/icons/icon-voltar.svg')"
                      alt=""
                      class="rp-modal-close-icon"
                    >
                    Voltar
                  </button>
                </div>

                <div class="rp-modal-body">
                  <!-- Divisória padrão de todos os modais de relatórios -->
                  <div class="rp-symptom-divider" />

                  <!-- Tratamento -->
                  <div
                    v-if="activeModal === 'treatment'"
                    class="rp-treat"
                  >
                    <div class="rp-treat-left">
                      <div class="rp-treat-doughnut">
                        <lila-doughnut-chart
                          v-if="treatment.data"
                          :chart-data="treatment.data"
                          :options="doughnutModalOptions"
                        />
                        <div class="rp-treat-center">
                          <span class="rp-treat-c-label">Total</span>
                          <span class="rp-treat-c-value">{{
                            treatment.total
                          }}</span>
                          <span class="rp-treat-c-label">Pacientes</span>
                        </div>
                      </div>
                    </div>

                    <div class="rp-treat-right">
                      <p class="rp-treat-desc">
                        Distribuição dos pacientes com tratamentos antineoplásico
                        realizados em relação ao seu total.
                      </p>
                      <div class="rp-treat-list">
                        <div
                          v-for="item in treatment.legend"
                          :key="item.label"
                          class="rp-treat-row"
                        >
                          <div class="rp-treat-name">
                            <span
                              class="rp-treat-dot"
                              :style="{ backgroundColor: item.color }"
                            />
                            {{ item.label }}
                          </div>
                          <div class="rp-treat-pct">
                            {{ fmtPct(item.percentage) }}%
                          </div>
                          <div class="rp-treat-count">
                            {{ item.count }}
                            {{ item.count === 1 ? "Paciente" : "Pacientes" }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- CIDs -->
                  <div
                    v-else-if="activeModal === 'cid'"
                    class="rp-cid"
                  >
                    <div class="rp-cid-container">
                      <div class="rp-cid-header">
                        <p
                          class="rp-cid-hcell rp-cid-arrow"
                          :class="cidArrowClass('cid')"
                          @click="orderCidColumn('cid')"
                        >
                          CID
                        </p>
                        <div class="rp-cid-hcell" />
                        <p
                          class="rp-cid-hcell rp-cid-arrow"
                          :class="cidArrowClass('percentage')"
                          @click="orderCidColumn('percentage')"
                        >
                          Porcentagem
                        </p>
                      </div>

                      <div class="rp-cid-list">
                        <div
                          v-for="cid in cidsOrdered"
                          :key="cid.code"
                          class="rp-cid-item"
                        >
                          <p class="rp-cid-cell rp-cid-code">
                            {{ cid.code }}
                          </p>
                          <p class="rp-cid-cell rp-cid-desc">
                            {{ cid.description || "—" }}
                          </p>
                          <div class="rp-cid-cell rp-cid-chart">
                            <div class="rp-cid-bar">
                              <div class="rp-cid-line">
                                <div
                                  class="rp-cid-fill"
                                  :style="{ width: `${cid.percentage}%` }"
                                />
                              </div>
                              <div class="rp-cid-pct">
                                {{ fmtPct(cid.percentage) }}%
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Evolução do Performance Status -->
                  <div
                    v-else-if="activeModal === 'performance'"
                    class="rp-ps"
                  >
                    <div class="rp-ps-filter">
                      <div class="rp-ps-field">
                        <label class="rp-ps-label">Início</label>
                        <date-picker
                          v-model:value="psFilter.start"
                          type="month"
                          format="MM/YYYY"
                          value-type="format"
                          :lang="datepickerLang"
                          :clearable="false"
                          :editable="false"
                          :disabled-date="disableFutureMonth"
                          input-class="rp-ps-input"
                          placeholder="MM/AAAA"
                          @change="buildPsEvolution"
                        />
                      </div>
                      <div class="rp-ps-field">
                        <label class="rp-ps-label">Final</label>
                        <date-picker
                          v-model:value="psFilter.end"
                          type="month"
                          format="MM/YYYY"
                          value-type="format"
                          :lang="datepickerLang"
                          :clearable="false"
                          :editable="false"
                          :disabled-date="disableFutureMonth"
                          input-class="rp-ps-input"
                          placeholder="MM/AAAA"
                          @change="buildPsEvolution"
                        />
                      </div>

                      <span class="rp-ps-vdivider" />

                      <div class="rp-ps-legend">
                        <label class="rp-ps-label">Legenda</label>
                        <ul>
                          <li><span class="rp-ps-dot bg-ps0" />PS0</li>
                          <li><span class="rp-ps-dot bg-ps1" />PS1</li>
                          <li><span class="rp-ps-dot bg-ps2" />PS2</li>
                          <li><span class="rp-ps-dot bg-ps3" />PS3</li>
                          <li><span class="rp-ps-dot bg-ps4" />PS4</li>
                        </ul>
                      </div>
                    </div>

                    <div class="rp-ps-chart-card">
                      <div class="rp-ps-bars">
                        <div
                          v-for="(month, i) in psEvolution"
                          :key="i"
                          class="rp-ps-col"
                        >
                          <div class="rp-ps-bar">
                            <div
                              v-for="seg in month.segments"
                              :key="seg.key"
                              class="rp-ps-seg"
                              :style="{
                                height: `${seg.percentage}%`,
                                backgroundColor: seg.color
                              }"
                              :title="`${seg.key}: ${fmtPct(seg.percentage)}%`"
                            />
                          </div>
                          <div class="rp-ps-month">
                            {{ month.label }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-else-if="activeModal === 'active'"
                    class="rp-doctors"
                  >
                    <div
                      class="rp-doc-tabs"
                      :class="{ 'is-inactive': doctorsTab === 'inactive' }"
                    >
                      <button
                        type="button"
                        class="rp-doc-tab"
                        :class="{ active: doctorsTab === 'active' }"
                        @click="doctorsTab = 'active'"
                      >
                        <span class="rp-doc-badge rp-doc-badge--on">
                          {{ activeDoctorsList.active.length }}
                        </span>
                        Profissionais Ativos
                      </button>
                      <button
                        type="button"
                        class="rp-doc-tab"
                        :class="{ active: doctorsTab === 'inactive' }"
                        @click="doctorsTab = 'inactive'"
                      >
                        <span class="rp-doc-badge">
                          {{ activeDoctorsList.inactive.length }}
                        </span>
                        Profissionais Inativos
                      </button>

                      <span class="rp-doc-indicator" />
                    </div>

                    <div class="rp-doc-content">
                      <div class="rp-doc-header">
                        <span>Nome</span>
                        <span>Contato</span>
                      </div>
                      <div class="rp-doc-list custom-scroll">
                        <div
                          v-for="(d, i) in currentDoctors"
                          :key="i"
                          class="rp-doc-item"
                        >
                          <span class="rp-doc-name">{{ d.name }}</span>
                          <span class="rp-doc-phone">{{ d.phone }}</span>
                        </div>
                        <div
                          v-if="currentDoctors.length === 0"
                          class="rp-doc-empty"
                        >
                          Nenhum resultado.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-else-if="activeModal === 'newpatients'"
                    class="rp-np"
                  >
                    <div class="rp-np-filter">
                      <div class="rp-ps-field">
                        <label class="rp-ps-label">Início</label>
                        <date-picker
                          v-model:value="npFilter.start"
                          type="month"
                          format="MM/YYYY"
                          value-type="format"
                          :lang="datepickerLang"
                          :clearable="false"
                          :editable="false"
                          :disabled-date="disableFutureMonth"
                          input-class="rp-ps-input"
                          placeholder="MM/AAAA"
                          @change="buildNewPatients"
                        />
                      </div>
                      <div class="rp-ps-field">
                        <label class="rp-ps-label">Final</label>
                        <date-picker
                          v-model:value="npFilter.end"
                          type="month"
                          format="MM/YYYY"
                          value-type="format"
                          :lang="datepickerLang"
                          :clearable="false"
                          :editable="false"
                          :disabled-date="disableFutureMonth"
                          input-class="rp-ps-input"
                          placeholder="MM/AAAA"
                          @change="buildNewPatients"
                        />
                      </div>
                      <div class="rp-ps-field rp-np-period">
                        <label class="rp-ps-label">Período</label>
                        <select
                          v-model="npFilter.period"
                          class="rp-np-select"
                          :disabled="npLoadingList"
                          @change="changeNpPeriod"
                        >
                          <option value="0">
                            Todos Pacientes
                          </option>
                          <option value="7">
                            Últimos 7 dias
                          </option>
                          <option value="30">
                            Últimos 30 dias
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="rp-np-body">
                      <div class="rp-np-chart-card">
                        <p class="rp-np-card-title">
                          Pacientes x Mês
                        </p>
                        <div class="rp-np-chart">
                          <lila-line-chart
                            v-if="npChart"
                            :chart-data="npChart"
                            :options="npLineOptions"
                          />
                        </div>
                      </div>

                      <div class="rp-np-list-card">
                        <p class="rp-np-card-title">
                          {{ npPeriodName }}
                        </p>
                        <div class="rp-np-list custom-scroll">
                          <div
                            v-for="(p, i) in npList"
                            :key="i"
                            class="rp-np-patient"
                          >
                            <div class="rp-np-p-left">
                              <p class="rp-np-p-name">
                                {{ p.name }}
                              </p>
                              <p class="rp-np-p-line">
                                {{ p.gender
                                }}<span v-if="p.age">, {{ p.age }}</span>
                              </p>
                              <p class="rp-np-p-line">
                                Cadastrado em: {{ p.registeredAt }}
                              </p>
                              <p
                                v-if="p.doctor"
                                class="rp-np-p-line"
                              >
                                Médico: {{ p.doctor }}
                              </p>
                            </div>
                            <div class="rp-np-p-right">
                              <p class="rp-np-p-cid">
                                {{ p.cid || "—" }}
                              </p>
                              <p
                                v-if="p.phone"
                                class="rp-np-p-phone"
                              >
                                {{ p.phone }}
                              </p>
                            </div>
                          </div>
                          <div
                            v-if="npList.length === 0"
                            class="rp-doc-empty"
                          >
                            Nenhum paciente.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-else-if="activeModal === 'pain'"
                    class="rp-symptom"
                  >
                    <p class="rp-symptom-total">
                      Total: {{ painPatients.total }} pacientes
                    </p>

                    <div class="rp-symptom-wrap">
                      <div class="rp-symptom-list">
                        <div
                          v-for="(p, i) in painPatients.list"
                          :key="`p-${i}`"
                          class="rp-symptom-item"
                        >
                          <div class="rp-symptom-left">
                            <p class="rp-symptom-name">
                              {{ p.name }}
                            </p>
                            <p class="rp-symptom-line">
                              {{ genderAge(p) }}
                            </p>
                            <p class="rp-symptom-line">
                              Cadastrado em:
                              {{ patientRegisteredAt(p.dateAndTime) }}
                            </p>
                            <p class="rp-symptom-line">
                              Médico: {{ p.doctor }}
                            </p>
                          </div>

                          <div class="rp-symptom-right">
                            <p
                              class="rp-symptom-intensity"
                              :class="painIntensityColor(p.intensity)"
                            >
                              Dor {{ painIntensityName(p.intensity) }}
                            </p>
                            <p class="rp-symptom-cid">
                              {{ p.cid }}
                            </p>
                            <p
                              v-if="p.phone"
                              class="rp-symptom-line"
                            >
                              {{ p.phone }}
                            </p>
                            <p class="rp-symptom-line">
                              Dor: EVA {{ p.intensity }}
                            </p>
                          </div>
                        </div>

                        <p
                          v-if="painPatients.list.length === 0"
                          class="rp-empty"
                        >
                          Nenhum paciente.
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Febre -->
                  <div
                    v-else-if="activeModal === 'fever'"
                    class="rp-symptom"
                  >
                    <p class="rp-symptom-total">
                      Total: {{ feverPatients.total }} pacientes
                    </p>

                    <div class="rp-symptom-wrap">
                      <div class="rp-symptom-list">
                        <div
                          v-for="(p, i) in feverPatients.list"
                          :key="`f-${i}`"
                          class="rp-symptom-item"
                        >
                          <div class="rp-symptom-left">
                            <p class="rp-symptom-name">
                              {{ p.name }}
                            </p>
                            <p class="rp-symptom-line">
                              {{ genderAge(p) }}
                            </p>
                            <p class="rp-symptom-line">
                              Cadastrado em:
                              {{ patientRegisteredAt(p.dateAndTime) }}
                            </p>
                            <p class="rp-symptom-line">
                              Médico: {{ p.doctor }}
                            </p>
                          </div>

                          <div class="rp-symptom-right">
                            <p
                              class="rp-symptom-intensity"
                              :class="feverIntensityColor(p.intensity)"
                            >
                              Febre {{ feverIntensityName(p.intensity) }}
                            </p>
                            <p class="rp-symptom-cid">
                              {{ p.cid }}
                            </p>
                            <p
                              v-if="p.phone"
                              class="rp-symptom-line"
                            >
                              {{ p.phone }}
                            </p>
                            <p class="rp-symptom-line">
                              Temperatura: {{ p.intensity }}°
                            </p>
                          </div>
                        </div>

                        <p
                          v-if="feverPatients.list.length === 0"
                          class="rp-empty"
                        >
                          Nenhum paciente.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </teleport>
      </div>
    </template>
  </lila-default-template>
</template>

<script>
import DefaultTemplate from "@components/template/DefaultTemplate.vue";
import DoughnutChart from "@components/shared/lila/DoughnutChart.vue";
import LineChart from "@components/shared/lila/LineChart.vue";
import UnitFilterSelect from "@components/management/UnitFilterSelect.vue";
import PalliativeCareReport from "@components/management/PalliativeCareReport.vue";
import useReportScope from "@composables/useReportScope";
import DatePicker from "vue-datepicker-next";
import "vue-datepicker-next/index.css";

// Locale PT-BR para o seletor de mês (mesmos nomes usados no Dashboard).
const DATEPICKER_LANG = {
  formatLocale: {
    months: [
      "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
      "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    ],
    monthsShort: [
      "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
      "Jul", "Ago", "Set", "Out", "Nov", "Dez"
    ]
  },
  monthBeforeYear: true
};

const PALETTE = [
  "#9a397d",
  "#4f3f82",
  "#c77bb0",
  "#3da0a3",
  "#79a556",
  "#aa9479",
  "#ce6241",
  "#d4145a"
];

export default {
  components: {
    "lila-default-template": DefaultTemplate,
    "lila-doughnut-chart": DoughnutChart,
    "lila-line-chart": LineChart,
    "date-picker": DatePicker,
    "unit-filter-select": UnitFilterSelect,
    "palliative-care-report": PalliativeCareReport
  },
  setup() {
    return { ...useReportScope() };
  },
  data() {
    return {
      // POC Cuidados Paliativos: "geral" = relatório atual; "cp" = métricas da PNCP (mock).
      reportViews: [
        { key: "geral", label: "Visão geral" },
        { key: "cp", label: "Cuidados paliativos" }
      ],
      reportView: this.$route.query.visao === "cp" ? "cp" : "geral",
      hasPalliativeCare: this.$Utilities.hasPalliativeCareAccess(this),
      lastUpdate: "",
      refreshing: false,
      scopeDeniedError: null,
      activeModal: null,
      datepickerLang: DATEPICKER_LANG,
      psFilter: { start: "", end: "" },
      psEvolution: [],
      doctorsTab: "active",
      // period = query `days` de api/report/last-patients ("0" = todos os pacientes).
      npFilter: { start: "", end: "", period: "0" },
      npLoadingList: false,
      npChart: null,
      npList: [],
      npLineOptions: {
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { display: false } },
          y: { beginAtZero: true, ticks: { precision: 0 } }
        }
      },
      summary: { doctor: "—", patient: "—" },
      performanceStatus: [],
      treatment: { data: null, legend: [], total: 0 },
      activeDoctorsPercentage: 0,
      newPatientsTotal: 0,
      cidDistribution: [],
      cidsAll: [],
      cidSort: { column: "percentage", cid: false, percentage: false },
      activeDoctorsList: { active: [], inactive: [] },
      painPatients: { total: 0, percentage: 0, list: [] },
      feverPatients: { total: 0, percentage: 0, list: [] },
      weeklyReports: { total: 0, percentage: 0 },
      reportedSymptoms: { total: 0, percentage: 0 },
      communicationDemands: { total: 0, average: 0 },
      errors: {
        performance: false,
        treatment: false,
        cids: false
      },
      doughnutOptions: {
        cutout: "68%",
        plugins: { legend: { display: false } }
      },
      doughnutModalOptions: {
        cutout: "85%",
        plugins: { legend: { display: false } }
      }
    };
  },
  computed: {
    // Título da lista de novos pacientes, conforme o período selecionado.
    npPeriodName() {
      return this.npFilter.period === "0"
        ? "Todos os novos Pacientes."
        : `Novos pacientes nos últimos ${this.npFilter.period} dias.`;
    },
    modalTitle() {
      const titles = {
        treatment: "Tratamentos Antineoplásico",
        cid: "Distribuição dos CIDS",
        performance: "Evolução do Performance Status",
        active: "Relação de profissionais ativos",
        newpatients: "Evolução do número de pacientes ativos",
        pain: "Pacientes com dor Moderada ou Grave",
        fever: "Pacientes com Febre superior a 37,8°"
      };
      return titles[this.activeModal] || "";
    },
    currentDoctors() {
      return this.doctorsTab === "active"
        ? this.activeDoctorsList.active
        : this.activeDoctorsList.inactive;
    },
    cidsOrdered() {
      const col = this.cidSort.column;
      if (!col) return this.cidsAll;
      const key = col === "cid" ? "code" : "percentage";
      const asc = this.cidSort[col];
      return this.cidsAll.slice().sort((a, b) => {
        if (a[key] > b[key]) return asc ? 1 : -1;
        if (a[key] < b[key]) return asc ? -1 : 1;
        return 0;
      });
    }
  },
  async created() {
    this.loadUnits();

    await this.reloadAllWidgets();
  },
  methods: {
    authHeader() {
      return {
        Authorization: `bearer ${
          this.$Session.get(this.$userSessionName).token.Value
        }`
      };
    },
    reportConfig() {
      return {
        headers: this.authHeader(),
        params: this.scopedParams()
      };
    },
    changeReportView(view) {
      if (this.reportView === view) return;
      this.reportView = view;
      // Mantém a visão na URL para a demo poder abrir direto em ?visao=cp.
      this.$router.replace({
        query: { ...this.$route.query, visao: view === "cp" ? "cp" : undefined }
      });
    },
    async changeUnit(unitId) {
      this.selectUnit(unitId);

      await this.reloadAllWidgets();
    },
    gv(obj, key) {
      return this.$Utilities.getValueIgnoreCase(obj, key);
    },
    pct(value) {
      return Math.round((Number(value) || 0) * 10) / 10;
    },
    fmtPct(value) {
      return (Number(value) || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 });
    },
    handleAxiosError(error) {
      if (this.isOutOfScopeError(error)) {
        this.scopeDeniedError = error;
        return;
      }

      if (error && error.response) {
        this.$Utilities.verifyToken(error.response.status);
      }
      console.error("Erro ao carregar dados do dashboard:", error);
    },

    async reloadAllWidgets(isScopeRetry = false) {
      // Evita clique duplo / recarga concorrente enquanto atualiza.
      if (this.refreshing) return;
      this.refreshing = true;
      this.scopeDeniedError = null;
      this.updateTimeOfLastUpdate();

      try {
        // A primeira chamada serve de "probe": se o backend não responder,
        // usamos dados mockados para conseguir testar a tela.
        const backendOk = await this.getSummary();

        if (backendOk) {
          // Aguarda todos os widgets terminarem antes de reabilitar o botão.
          await Promise.all([
            this.getPerformanceStatus(),
            this.getTreatments(),
            this.getActiveDoctors(),
            this.getNewPatients(),
            this.getCids(),
            this.getPain(),
            this.getFever(),
            this.getWeeklyReports(),
            this.getSymptoms(),
            this.getDemands()
          ]);
        } else if (!this.scopeDeniedError) {
          this.loadMockData();
        }
      } finally {
        this.refreshing = false;
      }

      // 403: a unidade saiu do escopo do usuário. Volta para a opção padrão
      // e recarrega uma única vez, sem deslogar.
      if (this.scopeDeniedError && !isScopeRetry) {
        const deniedError = this.scopeDeniedError;

        this.scopeDeniedError = null;
        this.resetToDefaultScope(deniedError);

        await this.reloadAllWidgets(true);
      }
    },

    updateTimeOfLastUpdate() {
      this.lastUpdate = `${this.$moment().format("DD/MM/YYYY H:mm")}h`;
    },

    // ------------------------------------------------------------------
    // Dados mockados (fallback quando os api/report/* não respondem).
    // TODO: remover quando o backend do dashboard estiver disponível.
    // ------------------------------------------------------------------
    loadMockData() {
      this.summary = { doctor: 77, patient: 329 };

      this.performanceStatus = [
        { key: "PS0", percentage: 85 },
        { key: "PS1", percentage: 5 },
        { key: "PS2", percentage: 7 },
        { key: "PS3", percentage: 2 },
        { key: "PS4", percentage: 2 }
      ];

      const treatItems = [
        { label: "1ª Linha", count: 49, percentage: 14.9, color: "#9a397d" },
        { label: "2ª Linha", count: 120, percentage: 36.5, color: "#3da0a3" },
        { label: "3ª Linha", count: 18, percentage: 5.5, color: "#4f3f82" },
        { label: "Outros", count: 142, percentage: 43.1, color: "#aa9479" }
      ];
      this.treatment = {
        data: {
          labels: treatItems.map((t) => t.label),
          datasets: [
            {
              data: treatItems.map((t) => t.count),
              backgroundColor: treatItems.map((t) => t.color),
              borderWidth: 0
            }
          ]
        },
        legend: treatItems.map((t) => ({
          label: t.label,
          color: t.color,
          percentage: t.percentage,
          count: t.count
        })),
        total: treatItems.reduce((sum, t) => sum + t.count, 0)
      };

      this.activeDoctorsPercentage = 78;
      this.newPatientsTotal = 12;

      this.cidsAll = [
        {
          code: "C50",
          description: "Neoplasia maligna da mama",
          percentage: 24
        },
        {
          code: "C34",
          description: "Neoplasia maligna do pulmão",
          percentage: 18
        },
        {
          code: "C61",
          description: "Neoplasia maligna da próstata",
          percentage: 14
        },
        {
          code: "C18",
          description: "Neoplasia maligna do cólon",
          percentage: 11
        },
        {
          code: "C73",
          description: "Neoplasia maligna da tireoide",
          percentage: 8
        },
        {
          code: "C25",
          description: "Neoplasia maligna do pâncreas",
          percentage: 6
        },
        {
          code: "C16",
          description: "Neoplasia maligna do estômago",
          percentage: 5
        }
      ];
      this.cidDistribution = this.cidsAll.slice(0, 4);

      this.painPatients = {
        total: 4,
        percentage: 12,
        list: [
          {
            name: "Ana Souza",
            gender: "Feminino",
            birthDate: "1989-03-14",
            dateAndTime: "2026-07-29T19:46:06",
            doctor: "Suporte 1.2",
            cid: "C50 . I . PS I",
            phone: "(11) 98888-7766",
            intensity: 7
          },
          {
            name: "Bruno Lima",
            gender: "Masculino",
            birthDate: "1978-11-02",
            dateAndTime: "2026-07-28T10:12:41",
            doctor: "Suporte 1.2",
            cid: "C34 . II . PS II",
            phone: "(11) 97777-6655",
            intensity: 8
          },
          {
            name: "Carla Mendes",
            gender: "Feminino",
            birthDate: "1995-06-21",
            dateAndTime: "2026-07-27T08:03:19",
            doctor: "Suporte 1.2",
            cid: "C18 . I . PS I",
            phone: "",
            intensity: 6
          },
          {
            name: "Diego Alves",
            gender: "Masculino",
            birthDate: "1990-01-09",
            dateAndTime: "2026-07-26T22:31:55",
            doctor: "Suporte 1.2",
            cid: "C97 . PS III",
            phone: "(11) 96666-5544",
            intensity: 9
          }
        ]
      };

      this.feverPatients = {
        total: 3,
        percentage: 5,
        list: [
          {
            name: "Elaine Rocha",
            gender: "Feminino",
            birthDate: "1988-04-12",
            dateAndTime: "2026-07-27T09:14:00",
            doctor: "Suporte 1.1",
            cid: "C50 . PS II",
            phone: "(11) 98888-1122",
            intensity: 38.5
          },
          {
            name: "Felipe Barros",
            gender: "Masculino",
            birthDate: "1975-11-30",
            dateAndTime: "2026-07-27T10:02:00",
            doctor: "Suporte 1.2",
            cid: "C34 . PS III",
            phone: "(11) 97777-3344",
            intensity: 39.1
          },
          {
            name: "Gabriela Pinto",
            gender: "Feminino",
            birthDate: "1995-06-03",
            dateAndTime: "2026-07-27T11:20:00",
            doctor: "Suporte 1.1",
            cid: "C18 . PS I",
            phone: "",
            intensity: 40.2
          }
        ]
      };

      this.weeklyReports = { total: 156, percentage: 64 };
      this.reportedSymptoms = { total: 42, percentage: 18 };
      this.communicationDemands = { total: 214, average: "2,4" };

      this.activeDoctorsList = {
        active: [
          { name: "Dra. Ana Beatriz", phone: "(11) 99999-0001" },
          { name: "Dr. Carlos Nunes", phone: "(11) 99999-0002" },
          { name: "Enf. Bruno Carvalho", phone: "(11) 99999-0003" },
          { name: "Psi. Diana Freitas", phone: "(11) 99999-0004" },
          { name: "Dr. Rafael Nogueira", phone: "(11) 99999-0005" }
        ],
        inactive: [
          { name: "Dr. Eduardo Lima", phone: "(11) 98888-0001" },
          { name: "Nut. Elaine Moraes", phone: "(11) 98888-0002" }
        ]
      };
    },

    openDetail(name) {
      this.activeModal = name;
      if (name === "performance") this.buildPsEvolution();
      if (name === "active") this.doctorsTab = "active";
      if (name === "newpatients") this.buildNewPatients();
      if (name === "cid") {
        this.cidSort = { column: "percentage", cid: false, percentage: false };
      }
    },

    orderCidColumn(column) {
      this.cidSort[column] =
        this.cidSort[column] === null ? true : !this.cidSort[column];
      this.cidSort.column = column;
    },
    cidArrowClass(column) {
      if (this.cidSort.column !== column) return "";
      return this.cidSort[column] ? "asc" : "desc";
    },

    async buildNewPatients() {
      if (!this.npFilter.start) {
        this.npFilter.start = this.$moment()
          .subtract(5, "months")
          .format("MM/YYYY");
      }
      if (!this.npFilter.end) {
        this.npFilter.end = this.$moment().format("MM/YYYY");
      }
      try {
        // O seletor já entrega o valor em MM/YYYY (mesmo formato da API).
        const start = this.npFilter.start;
        const end = this.npFilter.end;
        const chartRes = await this.$axios.get(
          `api/report/last-patients-all?startDate=${start}&endDate=${end}`,
          this.reportConfig()
        );
        const rows = Array.isArray(chartRes.data) ? chartRes.data : [];
        this.npChart = {
          labels: rows.map((r) => this.gv(r, "Month") || "—"),
          datasets: [
            {
              data: rows.map((r) => this.gv(r, "TotalPatients") ?? 0),
              borderColor: "#9a397d",
              backgroundColor: "rgba(154, 57, 125, 0.10)",
              pointBackgroundColor: "#9a397d",
              pointRadius: 5,
              tension: 0,
              fill: true
            }
          ]
        };

        await this.loadNpList();

        if (this.npList.length === 0 && rows.length === 0) this.loadNpMock();
      } catch (error) {
        this.loadNpMock();
      }
    },

    // Lista do modal, recortada pelo período selecionado (`days`).
    // O gráfico não depende do período, então só a lista recarrega.
    async loadNpList() {
      this.npLoadingList = true;
      try {
        const listRes = await this.$axios.get(
          `api/report/last-patients?days=${this.npFilter.period}&page=1&limit=50`,
          this.reportConfig()
        );
        const list = this.gv(listRes.data, "Data") || listRes.data || [];
        this.npList = (Array.isArray(list) ? list : []).map((p) =>
          this.mapNpItem(p)
        );
      } finally {
        this.npLoadingList = false;
      }
    },

    async changeNpPeriod() {
      this.npList = [];
      try {
        await this.loadNpList();
      } catch (error) {
        this.npList = [];
      }
    },

    mapNpItem(p) {
      return {
        name: this.gv(p, "Name") || "—",
        gender: this.gv(p, "Gender") || "",
        age: this.gv(p, "Age") || this.patientAge(this.gv(p, "BirthDate")),
        registeredAt:
          this.gv(p, "DateAndTime") || this.gv(p, "CreatedAt") || "",
        doctor: this.gv(p, "Doctor") || "",
        cid:
          this.gv(p, "CIDCode") ||
          this.gv(p, "CID") ||
          this.$Utilities.getPatientCid(p) ||
          "",
        phone: this.fmtPhone(this.gv(p, "Phone"))
      };
    },

    loadNpMock() {
      this.npChart = {
        labels: ["Fev", "Mar", "Abr", "Mai", "Jun", "Jul"],
        datasets: [
          {
            data: [2, 1, 8, 4, 1, 3],
            borderColor: "#9a397d",
            backgroundColor: "rgba(154, 57, 125, 0.10)",
            pointBackgroundColor: "#9a397d",
            pointRadius: 5,
            tension: 0,
            fill: true
          }
        ]
      };
      this.npList = [
        {
          name: "Renata Oliveira",
          gender: "Feminino",
          age: "4 anos",
          registeredAt: "21/02/2026 16:56:26",
          doctor: "Equipe Local",
          cid: "C01.III",
          phone: "(11) 97576-9053"
        },
        {
          name: "Lázaro Ramos",
          gender: "Masculino",
          age: "36 anos",
          registeredAt: "21/02/2026 17:55:28",
          doctor: "Suporte 1.2",
          cid: "C97",
          phone: ""
        },
        {
          name: "Gertrudes Silva",
          gender: "Feminino",
          age: "58 anos",
          registeredAt: "14/10/2026 12:28:01",
          doctor: "Equipe de Saúde A",
          cid: "C06.II",
          phone: "(54) 16348-6070"
        }
      ];
    },

    // Desabilita meses futuros no seletor (paridade com disableFutureDates).
    disableFutureMonth(date) {
      return this.$moment(date).isAfter(this.$moment(), "month");
    },

    async buildPsEvolution() {
      if (!this.psFilter.start) {
        this.psFilter.start = this.$moment()
          .subtract(5, "months")
          .format("MM/YYYY");
      }
      if (!this.psFilter.end) {
        this.psFilter.end = this.$moment().format("MM/YYYY");
      }
      try {
        // O seletor já entrega o valor em MM/YYYY (mesmo formato da API).
        const start = this.psFilter.start;
        const end = this.psFilter.end;
        const res = await this.$axios.get(
          `api/report/performance-status-all?startDate=${start}&endDate=${end}`,
          this.reportConfig()
        );
        const data = res.data || {};
        this.psEvolution = this.buildPsSeries(data);
        if (this.psEvolution.length === 0)
          this.psEvolution = this.mockPsEvolution();
      } catch (error) {
        this.psEvolution = this.mockPsEvolution();
      }
    },

    // Monta a série do gráfico de barras a partir da resposta agrupada por
    // mês ({ "mar./2026": [{ PS, Total, Percentage }, ...], ... }).
    //
    // A resposta traz Percentage como distribuição DENTRO do mês (soma ~100%),
    // o que encheria a barra inteira. Para reproduzir o Dashboard — em que a
    // altura reflete o VOLUME de pacientes do mês —, usamos a contagem (Total)
    // e normalizamos pelo total do período (Total ÷ total-geral).
    buildPsSeries(data) {
      const colors = {
        PS0: "#3da0a3",
        PS1: "#79a556",
        PS2: "#aa9479",
        PS3: "#ce6241",
        PS4: "#d4145a"
      };
      // Ordem de renderização (topo → base): PS0 (teal) fica na base da barra.
      const order = ["PS4", "PS3", "PS2", "PS1", "PS0"];

      // 1) Agrega o Total por PS em cada mês.
      const months = Object.keys(data || {}).map((month) => {
        const byPs = {};
        (Array.isArray(data[month]) ? data[month] : []).forEach((item) => {
          const key = String(this.gv(item, "PS") || "").toUpperCase();
          if (!key) return;
          byPs[key] = (byPs[key] || 0) + (Number(this.gv(item, "Total")) || 0);
        });
        return { label: this.capitalize(month), byPs };
      });

      // 2) Total geral do período (denominador comum a todos os meses).
      const grandTotal =
        months.reduce(
          (sum, m) =>
            sum + Object.values(m.byPs).reduce((s, v) => s + v, 0),
          0
        ) || 1;

      // 3) Altura de cada segmento = Total ÷ total-geral (proporcional ao
      //    volume real de pacientes, como no Dashboard).
      return months.map((m) => ({
        label: m.label,
        segments: order
          .filter((key) => m.byPs[key] > 0)
          .map((key) => ({
            key,
            percentage: (m.byPs[key] / grandTotal) * 100,
            color: colors[key]
          }))
      }));
    },

    capitalize(text) {
      const s = String(text || "");
      return s.charAt(0).toUpperCase() + s.slice(1);
    },

    fmtPhone(phone) {
      if (!phone) return "";
      const digits = String(phone).replace(/\D/g, "");
      if (digits.length === 11) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
      }
      if (digits.length === 10) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
      }
      return String(phone);
    },

    mockPsEvolution() {
      const labels = [
        "Fev./2026",
        "Mar./2026",
        "Abr./2026",
        "Mai./2026",
        "Jun./2026",
        "Jul./2026"
      ];
      // Ordem PS4→PS0: PS0 (teal) por último = base da barra.
      return labels.map((label, i) => ({
        label,
        segments: [
          { key: "PS4", percentage: 1.5, color: "#d4145a" },
          { key: "PS3", percentage: 1, color: "#ce6241" },
          { key: "PS2", percentage: 2.5, color: "#aa9479" },
          { key: "PS1", percentage: 2, color: "#79a556" },
          { key: "PS0", percentage: 8 + i * 4, color: "#3da0a3" }
        ]
      }));
    },
    closeDetail() {
      this.activeModal = null;
    },

    async getSummary() {
      try {
        const res = await this.$axios.get("api/report/total-doctorspatients", this.reportConfig());
        this.summary.doctor = this.gv(res.data, "Professionals") ?? 0;
        this.summary.patient = this.gv(res.data, "Patients") ?? 0;
        return true;
      } catch (error) {
        this.handleAxiosError(error);
        return false;
      }
    },

    async getPerformanceStatus() {
      try {
        const res = await this.$axios.get("api/report/performance-status", this.reportConfig());
        const map = {};
        (Array.isArray(res.data) ? res.data : []).forEach((item) => {
          const ps = String(this.gv(item, "PS") || "").toUpperCase();
          map[ps] = this.pct(this.gv(item, "Percentage"));
        });
        this.performanceStatus = ["PS0", "PS1", "PS2", "PS3", "PS4"].map(
          (key) => ({
            key,
            percentage: map[key] || 0
          })
        );
      } catch (error) {
        this.errors.performance = true;
        this.handleAxiosError(error);
      }
    },

    // Cor do tratamento pelo NOME da linha (idêntico ao getChartColor do
    // Dashboard), com fallback por índice para linhas fora da lista fixa.
    // Aceita "1ª Linha" e "1° Linha" (ordinal feminino/masculino).
    treatmentColor(line, index) {
      const key = (line || "").toString().toLowerCase();
      if (key.startsWith("1")) return "#9A397D"; // 1ª/1° Linha
      if (key.startsWith("2")) return "#3DA0A3"; // 2ª/2° Linha
      if (key.startsWith("3")) return "#4F3F82"; // 3ª/3° Linha
      if (key.includes("nenhum")) return "#30BED5"; // Nenhum Tratamento Neoplásico
      if (key.includes("outros")) return "#AA9479"; // Outros
      const fallback = [
        "#F6F7FB", "#00000029", "#3A3A3A", "#379E59", "#37489E", "#7B5CE8",
        "#B1859B", "#9E3737", "#9B9E37", "#9E7537", "#E67451", "#EE6992"
      ];
      return fallback[index] || "#3A3A3A";
    },

    async getTreatments() {
      try {
        const res = await this.$axios.get("api/report/treatments", this.reportConfig());
        const list = Array.isArray(res.data) ? res.data : [];
        this.treatment.data = {
          labels: list.map((item) => this.gv(item, "Line") || "—"),
          datasets: [
            {
              data: list.map(
                (item) =>
                  this.gv(item, "Total") ?? this.gv(item, "Percentage") ?? 0
              ),
              backgroundColor: list.map((item, index) =>
                this.treatmentColor(this.gv(item, "Line"), index)
              ),
              borderWidth: 0
            }
          ]
        };
        this.treatment.legend = list.map((item, index) => ({
          label: this.gv(item, "Line") || "—",
          color: this.treatmentColor(this.gv(item, "Line"), index),
          percentage: this.pct(this.gv(item, "Percentage")),
          count: Number(this.gv(item, "Total")) || 0
        }));
        this.treatment.total = this.treatment.legend.reduce(
          (sum, item) => sum + item.count,
          0
        );
      } catch (error) {
        this.errors.treatment = true;
        this.handleAxiosError(error);
      }
    },

    async getActiveDoctors() {
      try {
        const res = await this.$axios.get("api/report/last-active-units", this.reportConfig());
        this.activeDoctorsPercentage = this.pct(this.gv(res.data, "Percentage"));
        const activeList =
          this.gv(res.data, "ActiveProfessionals") ||
          this.gv(res.data, "ActiveDoctors") ||
          [];
        const inactiveList =
          this.gv(res.data, "InactiveProfessionals") ||
          this.gv(res.data, "InactiveDoctors") ||
          [];
        this.activeDoctorsList = {
          active: activeList.map((d) => ({
            name: this.gv(d, "Name") || "—",
            phone: this.fmtPhone(this.gv(d, "Phone"))
          })),
          inactive: inactiveList.map((d) => ({
            name: this.gv(d, "Name") || "—",
            phone: this.fmtPhone(this.gv(d, "Phone"))
          }))
        };
      } catch (error) {
        this.handleAxiosError(error);
      }
    },

    async getNewPatients() {
      try {
        const res = await this.$axios.get(
          "api/report/last-patients?days=21&page=1&limit=1",
          this.reportConfig()
        );
        const pagination = this.gv(res.data, "Pagination");
        this.newPatientsTotal = Number(
          (pagination &&
            (this.gv(pagination, "TotalInfo") ??
              this.gv(pagination, "Total") ??
              this.gv(pagination, "TotalItems"))) ??
            this.gv(res.data, "Total") ??
            0
        );
      } catch (error) {
        this.handleAxiosError(error);
      }
    },

    async getCids() {
      try {
        const res = await this.$axios.get("api/report/cid-distribution", this.reportConfig());
        this.cidsAll = (Array.isArray(res.data) ? res.data : [])
          .map((item) => ({
            code: this.gv(item, "CIDCode") || "—",
            description: this.gv(item, "Description") || "",
            percentage: this.pct(this.gv(item, "Percentage"))
          }))
          .sort((a, b) => b.percentage - a.percentage);
        this.cidDistribution = this.cidsAll.slice(0, 4);
      } catch (error) {
        this.errors.cids = true;
        this.handleAxiosError(error);
      }
    },

    async getPain() {
      try {
        const res = await this.$axios.get("api/report/pain", this.reportConfig());
        this.painPatients = {
          total: this.gv(res.data, "Total") ?? 0,
          percentage: this.pct(this.gv(res.data, "Percentage")),
          list: (this.gv(res.data, "PatientSymptoms") || []).map((p) =>
            this.mapSymptomPatient(p)
          )
        };
      } catch (error) {
        this.handleAxiosError(error);
      }
    },

    async getFever() {
      try {
        const res = await this.$axios.get("api/report/fever", this.reportConfig());
        this.feverPatients = {
          total: this.gv(res.data, "Total") ?? 0,
          percentage: this.pct(this.gv(res.data, "Percentage")),
          list: (this.gv(res.data, "PatientSymptoms") || []).map((p) =>
            this.mapSymptomPatient(p)
          )
        };
      } catch (error) {
        this.handleAxiosError(error);
      }
    },

    mapSymptomPatient(p) {
      const intensity = this.gv(p, "Intensity");
      return {
        name: this.gv(p, "Name") || "—",
        gender: this.gv(p, "Gender") || "",
        birthDate: this.gv(p, "BirthDate") || "",
        dateAndTime: this.gv(p, "DateAndTime") || "",
        doctor: this.gv(p, "Doctor") || "",
        cid: this.$Utilities.getPatientCid(p) || "",
        phone: this.fmtPhone(this.gv(p, "Phone")),
        intensity: intensity ?? "—"
      };
    },

    painIntensityName(intensity) {
      const value = Number(intensity);
      if (value <= 2) return "Leve";
      if (value <= 7) return "Moderada";
      return "Grave";
    },

    painIntensityColor(intensity) {
      const value = Number(intensity);
      if (value <= 2) return "rp-pain-mild";
      if (value <= 7) return "rp-pain-moderate";
      return "rp-pain-severe";
    },

    // Classificação da febre pela temperatura (°C). O card de origem
    // considera febre "superior a 37.8°".
    feverIntensityName(temperature) {
      const value = Number(temperature);
      if (value < 38.5) return "Leve";
      if (value <= 39.5) return "Moderada";
      return "Alta";
    },

    feverIntensityColor(temperature) {
      const value = Number(temperature);
      if (value < 38.5) return "rp-pain-mild";
      if (value <= 39.5) return "rp-pain-moderate";
      return "rp-pain-severe";
    },

    patientAge(birthDate) {
      if (!birthDate) return "";
      const years = this.$moment().diff(
        this.$moment(birthDate, "YYYY-MM-DD"),
        "years"
      );
      return `${years} anos`;
    },

    genderAge(p) {
      const age = this.patientAge(p.birthDate);
      return age ? `${p.gender}, ${age}` : p.gender;
    },

    patientRegisteredAt(dateAndTime) {
      if (!dateAndTime) return "";
      return this.$Utilities.formatDate(
        dateAndTime,
        "YYYY-MM-DDTHH:mm:ss",
        "DD/MM/YYYY HH:mm:ss"
      );
    },

    async getWeeklyReports() {
      try {
        const res = await this.$axios.get("api/report/last-weeklyreports", this.reportConfig());
        this.weeklyReports = {
          total: this.gv(res.data, "Total") ?? 0,
          percentage: this.pct(this.gv(res.data, "Percentage"))
        };
      } catch (error) {
        this.handleAxiosError(error);
      }
    },

    async getSymptoms() {
      try {
        const res = await this.$axios.get("api/report/last-symptoms", this.reportConfig());
        this.reportedSymptoms = {
          total: this.gv(res.data, "Total") ?? 0,
          percentage: this.pct(this.gv(res.data, "Percentage"))
        };
      } catch (error) {
        this.handleAxiosError(error);
      }
    },

    async getDemands() {
      try {
        const res = await this.$axios.get("api/report/last-demands", this.reportConfig());
        this.communicationDemands = {
          total: this.gv(res.data, "Total") ?? 0,
          average: this.gv(res.data, "Average") ?? 0
        };
      } catch (error) {
        this.handleAxiosError(error);
      }
    }
  }
};
</script>

<style lang="scss">
$lila-primary: #4f3f82;
$lila-accent: #9a397d;

/* Neutraliza os estilos do Bulma `.content` (o Lila Dashboard não fica dentro
   de .content; sem isto, .content injeta margens em p/ul/li e infla os cards). */
.reports-page p {
  font-size: 18px;
  line-height: 22px;
  margin: 0 !important;
}

.reports-page ul {
  margin: 0 !important;
  padding: 0 !important;
  list-style: none !important;
}

.reports-page li {
  margin: 0 !important;
}

.reports-page .text-has-font-12 {
  font-size: 12px;
  line-height: 16px;
}
.reports-page .text-has-font-14 {
  font-size: 14px;
  line-height: 18px;
}
.reports-page .text-has-font-16 {
  font-size: 16px;
  line-height: 20px;
}
.reports-page .text-has-font-35 {
  font-size: 35px;
  line-height: 39px;
}
.reports-page .text-has-font-40 {
  font-size: 40px;
  line-height: 44px;
}

.reports-page .has-text-dark-purple {
  /* Texto roxo (#4F3F82), igual ao has-text-dark-purple do dashboard —
     não confundir com has-bg-dark-purple (barras), que é magenta #9A397D. */
  color: $lila-primary !important;
}
.reports-page .has-bg-dark-purple {
  background-color: $lila-accent !important;
}

.reports-page .bg-ps0 {
  background-color: #3da0a3;
}
.reports-page .bg-ps1 {
  background-color: #79a556;
}
.reports-page .bg-ps2 {
  background-color: #aa9479;
}
.reports-page .bg-ps3 {
  background-color: #ce6241;
}
.reports-page .bg-ps4 {
  background-color: #d4145a;
}

/* Margens exatas do Lila Dashboard (px + !important, igual ao main.scss dele) */
.reports-page .mb1 {
  margin-bottom: 10px !important;
}
.reports-page .mb2 {
  margin-bottom: 20px !important;
}
.reports-page .mb4 {
  margin-bottom: 40px !important;
}
.reports-page .mb5 {
  margin-bottom: 50px !important;
}
.reports-page .mt1 {
  margin-top: 10px !important;
}

.reports-page {
  padding-top: 28px;
  padding-bottom: 20px;
}

/* Disposição (exata do Lila Dashboard) */
.reports-page .column-flex {
  display: flex;
  flex-direction: column;
}

.reports-page .column-flex .adjust-height {
  flex-grow: 1;
  flex-shrink: 0;
}

/* Pilha dos cards finos (dor, febre, relatos, sintomas, demandas): cada card
   cresce igualmente para preencher a altura da coluna (cards mais altos, como
   no Dashboard), alinhando o fim com a coluna esquerda (Performance/Tratamento).
   O conteúdo fica centralizado verticalmente dentro do card. */
.reports-page .demands-stack {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.reports-page .demands-stack > .widget {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
}

.reports-page .demands-stack > .widget > .widget-content {
  flex: 1;
  display: flex;
  align-items: center;
}

.reports-page .demands-stack > .widget > .widget-content > .chart-box,
.reports-page .demands-stack > .widget > .widget-content > .only-text {
  width: 100%;
}

/* Cabeçalho com botão atualizar */
/* Enquanto recarrega (carga inicial ou troca de unidade), os widgets ficam
   esmaecidos e inertes e o spinner do .loading segura a área. O filtro
   continua nítido para o usuário ver a unidade que escolheu. */
.reports-page .columns.reports-refreshing .widget {
  opacity: 0.35;
  pointer-events: none;
  transition: opacity 0.2s ease-in-out;
}

.reports-page .columns.reports-refreshing {
  min-height: 400px;
}

/* POC Cuidados Paliativos: seletor de visão */
.reports-page .report-view-switch {
  display: inline-flex;
  padding: 4px;
  border-radius: 999px;
  background-color: #ece9f5;
}

.reports-page .report-view-option {
  padding: 8px 20px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: $lila-primary;
  cursor: pointer;

  &.is-active {
    color: #ffffff;
    background-color: $lila-primary;
  }
}

.reports-page .reports-scope-alert {
  margin-bottom: 1.5rem;
  padding: 10px 40px 10px 16px;
  border-left: 5px solid #f14668;
  border-radius: 6px;
  color: #cc0f35;
  background-color: #feecf0;
}

.reports-page .reports-scope-alert,
.reports-page .reports-scope-alert p {
  font-size: 13px;
  line-height: 18px;
}

.reports-page .header-with-refresh {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.reports-page .header-with-refresh p.text-has-font-35 {
  color: #3a3a3a;
}

.reports-page .header-refresh-box {
  text-align: right;
}

.reports-page .btn-refresh {
  border: none;
  border-radius: 999px;
  background: $lila-accent;
  color: #ffffff;
  font-weight: 600;
  font-size: 11px;
  padding: 4px 12px;
  cursor: pointer;
  margin-bottom: 6px;
  transition: background 0.2s ease;
}

.reports-page .btn-refresh:hover {
  background: darken($lila-accent, 6%);
}

.reports-page .btn-refresh:disabled {
  opacity: 0.6;
  cursor: default;
}

.reports-page .btn-refresh:disabled:hover {
  background: $lila-accent;
}

/* Base dos widgets */
.reports-page .widget {
  &.full-height,
  &.full-height .widget-content {
    height: 100%;
  }

  .widget-content {
    padding: 20px 25px;
    border-radius: 15px;
    background-color: #ffffff;
    box-shadow: 2px 4px 7px 0 rgba(0, 0, 0, 0.1);
  }

  .widget-content > header {
    display: flex;
    align-items: center;
    margin-bottom: 25px;
    justify-content: space-between;

    &.only-btn {
      justify-content: flex-end;
    }

    h1 {
      color: #3a3a3a;
      font-size: 16px;
      line-height: 18px;
      margin: 0;
    }
  }

  .summary-divider {
    display: block;
    height: 2px;
    width: 15px;
    margin-left: auto;
    margin-right: auto;
    background-color: $lila-accent;
  }
}

.reports-page .widget-empty {
  color: #9a9a9a;
  font-size: 14px;
}

/* Centraliza verticalmente o conteúdo dos cards de % / novos pacientes */
.reports-page .widget.active-doctors-new-patients .widget-content {
  display: flex;
  flex-direction: column;
}

.reports-page .widget.active-doctors-new-patients .widget-content > div {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* Resumo Profissionais / Pacientes */
.reports-page .widget.doctors-patients-summary {
  .widget-content {
    display: flex;
    justify-content: flex-end;
    padding: 45px 40px;
    background-color: #ffffff;
    background-size: contain;
    background-position-x: 0%;
    background-repeat: no-repeat;
    background-image: url(~@assets/images/doctors-patients-summary.svg);
  }

  .summary-box {
    width: 50%;
    display: flex;
    justify-content: space-between;
  }

  .summary-qtd {
    font-size: 40px;
    line-height: 44px;
    /* Paridade com o dashboard: número herda o texto padrão do body
       (#3A3A3A) e peso light (has-text-weight-light = 300), não roxo. */
    color: #3a3a3a;
  }
}

/* Barras horizontais */
.reports-page .chart-box .vertical-line-chart:not(:last-child) {
  margin-bottom: 20px;
}

.reports-page .chart-box .vertical-line-chart .chart-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

.reports-page .chart-box .vertical-line-chart .chart-line,
.reports-page .chart-box .vertical-line-chart .chart-line .chart-value {
  display: block;
  height: 15px;
}

.reports-page .chart-box .vertical-line-chart .chart-line {
  width: 100%;
  border-radius: 4px;
  overflow: hidden;
  background-color: #f2f3f8;
}

.reports-page .chart-box .vertical-line-chart .chart-line .chart-value {
  border-radius: 4px;
  transition: width 1.4s ease-in-out 0.2s;
}

/* Rosca (Tratamento) — Chart.js */
.reports-page .doughnut-box {
  position: relative;
  width: 100%;
  max-width: 260px;
  aspect-ratio: 1 / 1;
  margin: 0 auto;
}

/* Legenda */
/* Legenda em 2 colunas, centralizada (igual ao Lila Dashboard) */
.reports-page .chart-legend ul {
  display: grid;
  grid-template-columns: auto auto;
  justify-content: center;
  column-gap: 28px;
  row-gap: 12px;
}

.reports-page .chart-legend ul li {
  padding-left: 24px;
  position: relative;
  font-size: 14px;
}

.reports-page .chart-legend ul li .legend-dot {
  position: absolute;
  top: calc(50% - 7px);
  left: 0;
  display: block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
}

/* Distribuição de CIDs */
.reports-page .widget.cid-distribution .vertical-line-chart {
  display: flex;
  align-items: center;
  gap: 10px;

  .chart-title {
    flex-basis: 55px;
    flex-shrink: 0;
  }

  .chart-line {
    flex-grow: 1;
  }

  > .chart-value {
    flex-basis: 45px;
    flex-shrink: 0;
    text-align: right;
  }
}

/* Widgets "patient-demands" */
.reports-page .widget.patient-demands {
  .widget-content {
    padding: 20px 25px;
  }

  .chart-box .vertical-line-chart,
  .only-text {
    display: flex;
    align-items: center;
  }

  .chart-box .vertical-line-chart > .chart-value,
  .chart-box .vertical-line-chart > .chart-title,
  .chart-box .vertical-line-chart .chart-line-box,
  .only-text .text-value,
  .only-text .text-title,
  .only-text .text-subtitle {
    flex-grow: 0;
    flex-shrink: 0;
  }

  .chart-box .vertical-line-chart > .chart-value,
  .only-text .text-value {
    flex-basis: 60px;
    margin-right: 5px;
  }

  .chart-box .vertical-line-chart > .chart-title,
  .only-text .text-title {
    flex-basis: calc(100% - 60px - 150px - 42px - 15px);
    margin-right: 5px;
  }

  .btn-widget-detail {
    flex-basis: 42px;
    flex-shrink: 0;
    justify-content: flex-end;
  }

  .only-text .text-subtitle {
    color: #8a8a8a;
  }

  .chart-box .vertical-line-chart {
    .chart-line,
    .chart-line .chart-value {
      height: 12px;
    }

    .chart-line-box {
      display: flex;
      align-items: center;
      flex-basis: 150px;
      margin-right: 5px;

      .chart-value {
        margin-right: 10px;
      }
    }
  }
}

/* Hover dos cards */
.reports-page .widget.hover-animated .widget-content {
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
}

.reports-page .widget.hover-animated .widget-content:hover {
  transform: translate(-2px, -2px);
  box-shadow: 3px 6px 12px 0 rgba(0, 0, 0, 0.16);
}

/* Botão de 3 pontos */
.reports-page .btn-widget-detail {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  border: none;
  background: none;
  padding: 4px;

  div {
    display: block;
    width: 6px;
    height: 6px;
    margin: 1px;
    border-radius: 10px;
    background-color: $lila-accent;
  }
}

/* Modal de detalhe */
.reports-page .rp-modal {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reports-page .rp-modal-bg {
  position: absolute;
  inset: 0;
  /* Mesmo overlay escuro padrão do Bulma (.modal-background) do Dashboard. */
  background: rgba(10, 10, 10, 0.86);
}

.reports-page .rp-modal-card {
  position: relative;
  z-index: 1;
  background: #ffffff;
  /* Mesmo raio padrão dos modais do Dashboard (Bulma .modal-card). */
  border-radius: 6px;
  width: min(680px, 92vw);
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}

.reports-page .rp-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 26px;
  /* A divisória agora é o elemento .rp-symptom-divider (padrão de todos
     os modais), então o cabeçalho não usa mais border-bottom. */
}

.reports-page .rp-modal-title {
  color: #3a3a3a;
  font-weight: 700;
  font-size: 35px;
  line-height: 39px;
}

.reports-page .rp-modal-close {
  display: flex;
  align-items: center;
  border: none;
  background: none;
  /* Paridade com o .close-modal do dashboard: peso regular (herda 400 do
     body, não negrito), 16px e magenta #9A397D. */
  font-size: 16px;
  font-weight: 400;
  color: $lila-accent;
  cursor: pointer;
}

.reports-page .rp-modal-close-icon {
  width: 28px;
  height: 28px;
  margin-right: 5px;
}

/* Modal largo (Tratamento) */
.reports-page .rp-modal-card--wide {
  /* Mesmas dimensões dos modais do Dashboard: largura calc(100% - 150px)
     e altura guiada pelo conteúdo (não fixa). */
  width: calc(100% - 150px);
  max-width: none;
  height: auto;
  max-height: calc(100vh - 40px);
  background: #f6f7fb;
}

.reports-page .rp-modal-card--wide .rp-modal-head {
  padding: 45px;
}

.reports-page .rp-modal-card--wide .rp-modal-body {
  padding: 0 45px 30px;
}

/* Modal Pacientes com dor / febre (igual ao Lila Dashboard) */
.reports-page .rp-modal-card--symptom .rp-modal-head {
  padding: 45px;
  border-bottom: none;
}

.reports-page .rp-modal-card--symptom .rp-modal-body {
  padding: 0 45px 30px;
}

.reports-page .rp-symptom-divider {
  height: 1px;
  width: 100%;
  /* Mesma divisória padrão dos demais modais (.horizontal-divider). */
  background-color: #bbbbbb;
  margin-bottom: 30px;
}

.reports-page .rp-symptom-total {
  margin: 0 0 30px;
  font-size: 16px;
  line-height: 20px;
  font-weight: 700;
  color: #9a397d;
  text-align: center;
}

.reports-page .rp-symptom-wrap {
  display: flex;
  justify-content: center;
}

.reports-page .rp-symptom-list {
  width: 66.6666%;
  max-height: 400px;
  overflow-y: scroll;
  padding-right: 10px;
  scroll-behavior: smooth;
}

.reports-page .rp-symptom-item {
  display: flex;
  justify-content: space-between;
  padding: 20px 0;
  border-radius: 15px;
  background-color: #ffffff;
}

.reports-page .rp-symptom-item:not(:last-child) {
  margin-bottom: 10px;
}

.reports-page .rp-symptom-left,
.reports-page .rp-symptom-right {
  padding-left: 20px;
  padding-right: 20px;
}

.reports-page .rp-symptom-left {
  width: 65%;
  border-right: 1px solid #c4c4c4;
}

.reports-page .rp-symptom-right {
  width: 35%;
}

.reports-page .rp-symptom-name {
  margin: 0 0 10px;
  font-size: 18px;
  line-height: 22px;
  font-weight: 600;
  color: #3a3a3a;
}

.reports-page .rp-symptom-line {
  margin: 0 0 5px;
  font-size: 16px;
  line-height: 20px;
  color: #3a3a3a;
}

.reports-page .rp-symptom-line:last-child {
  margin-bottom: 0;
}

.reports-page .rp-symptom-intensity {
  margin: 0 0 10px;
  font-size: 16px;
  line-height: 20px;
  font-weight: 700;
}

.reports-page .rp-symptom-cid {
  margin: 0 0 5px;
  font-size: 16px;
  line-height: 20px;
  font-weight: 600;
  color: #3a3a3a;
}

.reports-page .rp-pain-mild {
  color: #46dbd4;
}

.reports-page .rp-pain-moderate {
  color: #dfc12a;
}

.reports-page .rp-pain-severe {
  color: #dc2121;
}

.reports-page .rp-symptom-list::-webkit-scrollbar {
  width: 8px;
}

.reports-page .rp-symptom-list::-webkit-scrollbar-track {
  background-color: #4f3f82;
  border-radius: 50px;
  margin: 20px 0;
}

.reports-page .rp-symptom-list::-webkit-scrollbar-thumb {
  background-color: #9a397d;
  border-radius: 50px;
}

/* Modal de Distribuição de CIDs: altura ajustada ao conteúdo (mais baixo) */
.reports-page .rp-modal-card--cid {
  height: auto;
  max-height: 80vh;
}

/* Modal Distribuição dos CIDS (igual ao Lila Dashboard) */
.reports-page .rp-cid {
  display: flex;
  justify-content: center;
}

.reports-page .rp-cid-container {
  width: 66.6666%;
}

.reports-page .rp-cid-header {
  display: flex;
  padding: 10px 40px 10px 35px;
}

.reports-page .rp-cid-hcell {
  font-size: 16px;
  line-height: 20px;
  font-weight: 700;
  color: #3a3a3a;
}

.reports-page .rp-cid-hcell:nth-child(1),
.reports-page .rp-cid-cell:nth-child(1) {
  width: 100px;
}

.reports-page .rp-cid-hcell:nth-child(2),
.reports-page .rp-cid-cell:nth-child(2) {
  width: calc(100% - 300px);
}

.reports-page .rp-cid-hcell:nth-child(3),
.reports-page .rp-cid-cell:nth-child(3) {
  width: 200px;
}

.reports-page .rp-cid-arrow {
  position: relative;
  cursor: pointer;
}

.reports-page .rp-cid-arrow::before,
.reports-page .rp-cid-arrow::after {
  content: "";
  position: absolute;
  right: 0;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
}

.reports-page .rp-cid-arrow::before {
  top: 3px;
  border-bottom: 6px solid #959595;
}

.reports-page .rp-cid-arrow::after {
  bottom: 3px;
  border-top: 6px solid #959595;
}

.reports-page .rp-cid-arrow.asc::before {
  border-bottom-color: #9a397d;
}

.reports-page .rp-cid-arrow.desc::after {
  border-top-color: #9a397d;
}

.reports-page .rp-cid-list {
  overflow-y: auto;
  max-height: 400px;
  padding: 10px 35px;
  border-radius: 15px;
  background-color: #ffffff;
}

.reports-page .rp-cid-item {
  display: flex;
  align-items: center;
  padding: 15px 0;
}

.reports-page .rp-cid-item:not(:last-child) {
  border-bottom: 1px solid #c4c4c4;
}

.reports-page .rp-cid-cell {
  font-size: 16px;
  line-height: 20px;
  color: #3a3a3a;
  margin: 0;
}

.reports-page .rp-cid-code {
  font-weight: 400;
}

.reports-page .rp-cid-desc {
  color: #3a3a3a;
  font-weight: 400;
}

.reports-page .rp-cid-chart {
  display: flex;
  align-items: center;
}

.reports-page .rp-cid-bar {
  display: flex;
  align-items: center;
  width: 100%;
}

.reports-page .rp-cid-line {
  flex-grow: 1;
  margin-right: 10px;
  height: 12px;
  border-radius: 4px;
  overflow: hidden;
  background-color: #f2f3f8;
}

.reports-page .rp-cid-fill {
  height: 12px;
  border-radius: 4px;
  background-color: #9a397d;
  transition: width 1.4s ease-in-out 0.2s;
}

.reports-page .rp-cid-pct {
  flex-basis: 45px;
  flex-shrink: 0;
  text-align: right;
  font-size: 16px;
  line-height: 20px;
  color: #3a3a3a;
}

/* Custom scrollbar magenta (igual ao dashboard: .custom-scroll) */
.reports-page .rp-cid-list::-webkit-scrollbar {
  width: 8px;
}

.reports-page .rp-cid-list::-webkit-scrollbar-track {
  background-color: #4f3f82;
  border-radius: 50px;
  margin: 20px 0;
}

.reports-page .rp-cid-list::-webkit-scrollbar-thumb {
  border-radius: 50px;
  background-color: #9a397d;
}

.reports-page .rp-treat {
  display: flex;
  gap: 30px;
  height: 100%;
}

.reports-page .rp-treat-left,
.reports-page .rp-treat-right {
  flex: 1;
  min-width: 0;
  background: #ffffff;
  border-radius: 15px;
  box-shadow: 2px 4px 7px 0 rgba(0, 0, 0, 0.1);
  padding: 50px;
}

.reports-page .rp-treat-left {
  display: flex;
  align-items: center;
  justify-content: center;
}

.reports-page .rp-treat-doughnut {
  position: relative;
  width: 100%;
  max-width: 450px;
  aspect-ratio: 1 / 1;
  margin: 0 auto;
}

.reports-page .rp-treat-center {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.reports-page .rp-treat-c-label {
  color: #3a3a3a;
  font-size: 20px;
  line-height: 24px;
  font-weight: 500;
}

.reports-page .rp-treat-c-value {
  color: #3a3a3a;
  font-size: 82px;
  line-height: 86px;
  font-weight: 500;
}

.reports-page .rp-treat-desc {
  color: #3a3a3a;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 30px !important;
}

.reports-page .rp-treat-row {
  display: flex;
  align-items: center;
  padding: 15px 0;
  font-size: 16px;
  font-weight: 500;
}

.reports-page .rp-treat-row:not(:last-child) {
  border-bottom: 1px solid #c4c4c4;
}

.reports-page .rp-treat-name {
  width: 55%;
  padding-left: 20px;
  position: relative;
  color: #3a3a3a;
}

.reports-page .rp-treat-dot {
  position: absolute;
  left: 0;
  top: calc(50% - 7px);
  width: 14px;
  height: 14px;
  border-radius: 3px;
}

.reports-page .rp-treat-pct {
  width: 15%;
  text-align: center;
  color: #3a3a3a;
}

.reports-page .rp-treat-count {
  width: 30%;
  text-align: right;
  color: #3a3a3a;
}

.reports-page .rp-ps-filter {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 24px;
}

.reports-page .rp-ps-field {
  display: flex;
  flex-direction: column;
}

.reports-page .rp-ps-label {
  font-size: 14px;
  line-height: 18px;
  color: #3a3a3a;
  /* Mesmo recuo do label do campo padrão (.label { margin-left: 20px }). */
  margin-left: 20px;
  margin-bottom: 8px;
}

/* Novos Pacientes: os campos crescem para preencher a linha
   (2 datas + Período), como no Dashboard. */
.reports-page .rp-np-filter .rp-ps-field {
  flex: 1;
  min-width: 0;
}

/* Performance Status: campos de data com largura fixa (~240px);
   a legenda fica à direita da divisória vertical, como no Dashboard. */
.reports-page .rp-ps-filter .rp-ps-field {
  flex: 0 1 240px;
}

.reports-page .rp-ps-input {
  position: relative;
  width: 100%;
  border: none;
  border-radius: 26px;
  height: 40px;
  padding: 0 20px 0 50px;
  font-size: 16px;
  color: #3a3a3a;
  box-shadow: 0 2px 6px rgba(79, 63, 130, 0.1);
  background: #ffffff
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%234f3f82' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='4' width='18' height='18' rx='2'/%3E%3Cline x1='16' y1='2' x2='16' y2='6'/%3E%3Cline x1='8' y1='2' x2='8' y2='6'/%3E%3Cline x1='3' y1='10' x2='21' y2='10'/%3E%3C/svg%3E")
    no-repeat 20px center;
  background-size: 20px 20px;
}

/* Esconde o ícone nativo do date picker, mantendo apenas o ícone roxo
   (background). O indicador continua clicável cobrindo todo o campo. */
.reports-page .rp-ps-input::-webkit-calendar-picker-indicator {
  position: absolute;
  inset: 0;
  width: auto;
  height: auto;
  margin: 0;
  padding: 0;
  opacity: 0;
  cursor: pointer;
}

/* Seletor de mês (vue-datepicker-next): o campo assume o visual arredondado
   do .rp-ps-input (via input-class) e mostra MM/AAAA, igual ao Dashboard.
   Ajustamos o wrapper para ocupar a largura toda e escondemos o ícone
   próprio do componente (já existe o ícone roxo no fundo do input). */
.reports-page .rp-ps-field .mx-datepicker {
  width: 100%;
}

.reports-page .rp-ps-field .mx-input-wrapper .mx-icon-calendar,
.reports-page .rp-ps-field .mx-input-wrapper .mx-icon-clear {
  display: none;
}

/* O .rp-ps-input já traz border/height/shadow; garante que o mx-input não
   sobreponha esses valores. */
.reports-page .rp-ps-field .mx-input.rp-ps-input {
  box-shadow: 0 2px 6px rgba(79, 63, 130, 0.1);
}

/* Popup do seletor com o roxo do Lila nas seleções. */
.mx-datepicker-popup .mx-table-month .cell.active,
.mx-datepicker-popup .mx-table-year .cell.active {
  background-color: #4f3f82;
  color: #ffffff;
}

.reports-page .rp-ps-vdivider {
  width: 1px;
  height: 20px;
  /* Mesma divisória vertical do filtro no Dashboard (.vertical-divider). */
  background: #bbbbbb;
  margin: 15px 45px 0;
}

.reports-page .rp-ps-legend ul {
  display: flex;
  gap: 20px;
  align-items: center;
  margin-top: 6px;
}

.reports-page .rp-ps-legend li {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #3a3a3a;
}

.reports-page .rp-ps-dot {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  margin-right: 6px;
  display: inline-block;
}

.reports-page .rp-ps-chart-card {
  background: #ffffff;
  border-radius: 15px;
  box-shadow: 2px 4px 7px 0 rgba(0, 0, 0, 0.1);
  padding: 50px;
  min-height: 440px;
}

.reports-page .rp-ps-bars {
  display: flex;
  justify-content: center;
  gap: 25px;
}

.reports-page .rp-ps-col {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
}

/* Barra = bloco de 300px; os segmentos empilham de cima para baixo, como
   no Dashboard (.chart-bar). Meses com menos pacientes ficam mais baixos. */
.reports-page .rp-ps-bar {
  height: 300px;
  margin-bottom: 20px;
}

.reports-page .rp-ps-seg {
  display: block;
  width: 30px;
  margin-left: auto;
  margin-right: auto;
  /* Mesma micro-interação do Dashboard (.chart-bar div:hover). */
  transition: transform 0.3s ease-in-out;
}

.reports-page .rp-ps-seg:hover {
  transform: scale(1.1);
}

.reports-page .rp-ps-seg:not(:last-child) {
  margin-bottom: 1px;
}

.reports-page .rp-ps-seg:first-child {
  border-radius: 3px 3px 0 0;
}

.reports-page .rp-ps-seg:last-child {
  border-radius: 0 0 3px 3px;
}

.reports-page .rp-ps-month {
  font-size: 14px;
  font-weight: 500;
  color: #3a3a3a;
  text-align: center;
}

/* Modal: Evolução do número de pacientes ativos */
.reports-page .rp-np-filter {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 24px;
}

.reports-page .rp-np-period {
  /* Período ocupa ~50% da linha (o mesmo que as duas datas somadas). */
  flex: 2;
}

.reports-page .rp-np-select {
  width: 100%;
  height: 40px;
  border: none;
  border-radius: 26px;
  padding: 0 44px 0 20px;
  font-size: 16px;
  color: #3a3a3a;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background: #ffffff
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%234f3f82' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")
    no-repeat right 18px center;
  background-size: 18px 18px;
  box-shadow: 0 2px 6px rgba(79, 63, 130, 0.1);
  cursor: pointer;
}

.reports-page .rp-np-body {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.reports-page .rp-np-chart-card,
.reports-page .rp-np-list-card {
  flex: 1;
  min-width: 0;
}

.reports-page .rp-np-chart-card {
  background: #ffffff;
  border-radius: 15px;
  box-shadow: 2px 4px 7px 0 rgba(0, 0, 0, 0.1);
  padding: 25px;
}

.reports-page .rp-np-card-title {
  color: #3a3a3a;
  font-weight: 600;
  margin-bottom: 20px !important;
}

.reports-page .rp-np-chart {
  position: relative;
  height: 380px;
}

.reports-page .rp-np-list {
  max-height: 400px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-right: 6px;
}

.reports-page .rp-np-patient {
  display: flex;
  justify-content: space-between;
  background: #ffffff;
  border-radius: 15px;
  box-shadow: 2px 4px 7px 0 rgba(0, 0, 0, 0.1);
  padding: 18px 22px;
}

.reports-page .rp-np-p-name {
  color: #3a3a3a;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 6px !important;
}

.reports-page .rp-np-p-line {
  color: #3a3a3a;
  font-size: 16px;
  line-height: 20px;
}

.reports-page .rp-np-p-right {
  text-align: right;
  padding-left: 22px;
  border-left: 1px solid #c4c4c4;
  min-width: 150px;
}

.reports-page .rp-np-p-cid {
  color: #3a3a3a;
  font-weight: 600;
  margin-bottom: 6px !important;
}

.reports-page .rp-np-p-phone {
  color: $lila-primary;
  font-size: 14px;
}

.reports-page .rp-modal-body {
  padding: 24px 26px;
  overflow-y: auto;
  flex: 1;
}

/* Modal: Relação de profissionais ativos (abas + tabela) */
.reports-page .rp-doctors {
  max-width: 66%;
  margin: 0 auto;
}

.reports-page .rp-doc-tabs {
  position: relative;
  display: flex;
  border-bottom: 1px solid #707070;
  margin-bottom: 26px;
}

/* Indicador deslizante sob a aba ativa */
.reports-page .rp-doc-indicator {
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 50%;
  height: 4px;
  background: $lila-accent;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.reports-page .rp-doc-tabs.is-inactive .rp-doc-indicator {
  transform: translateX(100%);
}

.reports-page .rp-doc-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 0;
  border: none;
  background: none;
  font-size: 16px;
  font-weight: 500;
  color: #3a3a3a;
  cursor: pointer;
  transition: color 0.2s ease;
}

.reports-page .rp-doc-tab.active {
  /* Aba ativa em magenta (#9A397D), como no padrão do Dashboard (.tab-active). */
  color: $lila-accent;
  font-weight: 700;
}

.reports-page .rp-doc-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #9e9e9e;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
}

/* Ativos: sempre magenta; Inativos: sempre cinza (independente da aba). */
.reports-page .rp-doc-badge--on {
  background: $lila-accent;
}

.reports-page .rp-doc-header {
  display: flex;
  justify-content: space-between;
  padding: 10px 43px 10px 35px;
  color: #a2a2a2;
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
}

.reports-page .rp-doc-list {
  background: #ffffff;
  border-radius: 15px;
  max-height: 400px;
  overflow-y: auto;
  padding: 10px 35px;
}

.reports-page .rp-doc-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid #c4c4c4;
  font-size: 16px;
  font-weight: 500;
}

.reports-page .rp-doc-item:last-child {
  border-bottom: none;
}

.reports-page .rp-doc-name {
  color: #3a3a3a;
}

.reports-page .rp-doc-phone {
  color: #3a3a3a;
}

.reports-page .rp-doc-empty {
  padding: 20px 22px;
  color: #9a9a9a;
  text-align: center;
}

/* Estado vazio das listas de Dor / Febre */
.reports-page .rp-empty {
  padding: 20px 0;
  text-align: center;
  color: #9a9a9a;
}
</style>
